import React, { useMemo } from "react";

const PALETTE = ["#0E5FFF", "#FF9500", "#1F8A3B", "#FF3B30", "#7C3AED", "#0a0a0b", "#FFCC00"];

/**
 * Simple SVG pie chart with a legend. Uses standard trig math — no deps.
 */
export default function PieWidget({ definition, payload, onDrilldown }) {
  const data = payload?.data || {};
  const slices = Array.isArray(data.slices) ? data.slices : [];
  const total = slices.reduce((acc, s) => acc + Number(s.value || 0), 0) || 1;
  const clickable = !!payload?.drilldown;

  const arcs = useMemo(() => {
    let start = 0;
    const cx = 50;
    const cy = 50;
    const r = 40;
    return slices.map((s, i) => {
      const frac = Number(s.value || 0) / total;
      const end = start + frac * 2 * Math.PI;
      const x1 = cx + r * Math.sin(start);
      const y1 = cy - r * Math.cos(start);
      const x2 = cx + r * Math.sin(end);
      const y2 = cy - r * Math.cos(end);
      const large = frac > 0.5 ? 1 : 0;
      const d = `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
      start = end;
      return { d, color: PALETTE[i % PALETTE.length], ...s };
    });
  }, [slices, total]);

  return (
    <div
      data-testid={`widget-pie-${definition.id}`}
      className="bg-white border border-[var(--border)] p-5 min-h-[240px] flex flex-col"
    >
      <div className="mb-3">
        <div className="font-mono-tech text-[0.6rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
          DISTRIBUCIÓN
        </div>
        <div className="font-display font-bold text-base tracking-tight mt-0.5">
          {definition.title}
        </div>
      </div>
      {slices.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-[var(--text-secondary)]">
          Sin datos
        </div>
      ) : (
        <div className="flex-1 flex items-center gap-4">
          <svg viewBox="0 0 100 100" className="w-32 h-32 shrink-0">
            {arcs.map((a, i) => (
              <path key={i} d={a.d} fill={a.color} />
            ))}
          </svg>
          <ul className="flex-1 space-y-1.5 min-w-0">
            {arcs.map((a, i) => (
              <li key={i} className="flex items-center gap-2 text-xs">
                <span
                  className="w-3 h-3 shrink-0"
                  style={{ background: a.color }}
                  aria-hidden
                />
                <span className="flex-1 truncate">{a.label}</span>
                <span className="font-mono-tech tabular-nums font-bold">
                  {a.value}
                </span>
                <span className="font-mono-tech text-[0.65rem] text-[var(--text-secondary)]">
                  {((a.value / total) * 100).toFixed(0)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {clickable && (
        <button
          type="button"
          onClick={() => onDrilldown?.(definition, payload)}
          className="mt-3 font-mono-tech text-[0.65rem] font-bold tracking-wider text-[#0E5FFF] hover:underline text-left"
        >
          {payload.drilldown.label || "Ver detalle →"}
        </button>
      )}
    </div>
  );
}
